import {
  PROJECT_PERSIST_REASON,
  type ProjectPersistReason,
} from "./projectPersistReason";

export const REVISION_AUTOSAVE_DEBOUNCE_MS = 2_000;
export const REVISION_AUTOSAVE_MAX_WAIT_MS = 10_000;
export const REVISION_AUTOSAVE_RETRY_BASE_MS = 2_000;
export const REVISION_AUTOSAVE_RETRY_MAX_MS = 30_000;

export interface RevisionSaveRequest {
  reason: ProjectPersistReason;
  saveAttemptId?: string;
  blocking: boolean;
  dirtySpaceIndices: number[];
}

export interface RevisionAutoSaveCoordinator {
  schedule: (spaceIndex: number) => void;
  flush: (request: Omit<RevisionSaveRequest, "dirtySpaceIndices">) => Promise<boolean>;
  pause: () => void;
  resume: () => void;
  dispose: () => void;
}

export function createRevisionAutoSaveCoordinator(
  save: (request: RevisionSaveRequest) => Promise<boolean>,
): RevisionAutoSaveCoordinator {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let firstMutationAt: number | null = null;
  let inFlight: Promise<boolean> | null = null;
  let pending = false;
  let queuedRequest: Omit<RevisionSaveRequest, "dirtySpaceIndices"> | null = null;
  let paused = false;
  let retryAttempt = 0;
  const dirtySpaceIndices = new Set<number>();

  const clearTimer = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };

  /**
   * 저장이 실패하면 dirty Space가 그대로 남는다. 여기서 다시 타이머를 걸지 않으면
   * 사용자가 추가로 편집하기 전까지 그 변경은 영영 커밋되지 않는다.
   *
   * Import 직후처럼 Unity가 아직 정착 중이라 한 번 실패하는 경우가 실제로 있으므로
   * backoff로 다시 시도한다. 편집 내용은 Unity가 들고 있고 사용자는 아직 Editor에
   * 있으므로 포기하지 않고 상한 간격으로 계속 재시도한다.
   */
  const armRetry = () => {
    clearTimer();
    const delay = Math.min(
      REVISION_AUTOSAVE_RETRY_MAX_MS,
      REVISION_AUTOSAVE_RETRY_BASE_MS * 2 ** retryAttempt,
    );
    retryAttempt += 1;
    timer = setTimeout(
      () =>
        void run({
          reason: PROJECT_PERSIST_REASON.REVISION_AUTO_SAVE,
          blocking: false,
        }),
      delay,
    );
  };

  const run = async (
    request: Omit<RevisionSaveRequest, "dirtySpaceIndices">,
  ): Promise<boolean> => {
    if (
      request.reason === PROJECT_PERSIST_REASON.REVISION_AUTO_SAVE &&
      !pending &&
      dirtySpaceIndices.size === 0
    ) {
      return inFlight ?? true;
    }
    clearTimer();
    firstMutationAt = null;
    if (paused) return false;
    pending = true;
    queuedRequest = request;
    if (inFlight) return inFlight;

    const drain = (async () => {
      let saved = true;
      while (pending && !paused) {
        pending = false;
        const nextRequest = queuedRequest ?? request;
        queuedRequest = null;
        const currentDirtySpaceIndices = Array.from(dirtySpaceIndices);
        dirtySpaceIndices.clear();
        saved = await save({
          ...nextRequest,
          dirtySpaceIndices: currentDirtySpaceIndices,
        });
        if (!saved) {
          currentDirtySpaceIndices.forEach((index) => dirtySpaceIndices.add(index));
          break;
        }
      }
      if (saved) retryAttempt = 0;
      return saved;
    })();
    inFlight = drain.finally(() => {
      inFlight = null;
      if (!paused && dirtySpaceIndices.size > 0) armRetry();
    });
    return inFlight;
  };

  const schedule = (spaceIndex: number): void => {
    if (paused) return;
    if (Number.isInteger(spaceIndex) && spaceIndex >= 0) {
      dirtySpaceIndices.add(spaceIndex);
    }
    pending = true;
    retryAttempt = 0;
    if (inFlight) return;
    const now = Date.now();
    firstMutationAt ??= now;
    clearTimer();
    const dueAt = Math.min(
      now + REVISION_AUTOSAVE_DEBOUNCE_MS,
      firstMutationAt + REVISION_AUTOSAVE_MAX_WAIT_MS,
    );
    timer = setTimeout(
      () => void run({ reason: "revision_auto_save", blocking: false }),
      Math.max(0, dueAt - now),
    );
  };

  return {
    schedule,
    flush: run,
    pause() {
      paused = true;
      clearTimer();
    },
    resume() {
      paused = false;
    },
    dispose() {
      paused = true;
      clearTimer();
    },
  };
}

let activeCoordinator: RevisionAutoSaveCoordinator | null = null;

export const registerRevisionAutoSaveCoordinator = (
  coordinator: RevisionAutoSaveCoordinator,
): (() => void) => {
  activeCoordinator = coordinator;
  return () => {
    if (activeCoordinator === coordinator) activeCoordinator = null;
    coordinator.dispose();
  };
};

export const isRevisionAutoSaveEnabled = (): boolean =>
  activeCoordinator !== null;

/**
 * Editor 세션 종료 시 호출한다. 이 세션의 예약·backoff 재시도를 모두 멈추고 autosave를
 * 비활성화한다. 세션이 끝난 뒤 도착하는 in-flight 저장의 실패가 `armRetry`로 새 attempt를
 * 시작해 모듈 상태를 다시 오염시키지 않게 한다. hook의 unregister와 겹쳐도 dispose는 멱등이다.
 */
export const disposeActiveRevisionAutoSaveCoordinator = (): void => {
  activeCoordinator?.dispose();
  activeCoordinator = null;
};

export const notifyRevisionAutoSaveMutation = (spaceIndex: number): void => {
  activeCoordinator?.schedule(spaceIndex);
};

export const requestRevisionAutoSaveFlush = (
  request: Omit<RevisionSaveRequest, "dirtySpaceIndices">,
): Promise<boolean> => activeCoordinator?.flush(request) ?? Promise.resolve(false);
